import {EventsHash, ViewOptions} from 'backbone';
import * as d3 from 'd3';
import $ from 'jquery';
import React from 'react';

import {firefox} from 'BenchlingUtil';
import {ProteinAlignmentKind} from 'constants/AlignmentConstants';
import {ALIGNMENT_TYPE} from 'constants/MapsConstants';
import AlignmentRowInfoWrapper, {AlignmentRowInfoWrapperProps} from 'molbio/maps/alignment/AlignmentRowInfo';
import SequenceAlignmentTabView from 'molbio/maps/alignment/SequenceAlignmentTabView';
import Slice from 'molbio/maps/linear/Slice';
import {SliceView} from 'molbio/maps/linear/SliceView';
import {AlignmentMapState} from 'molbio/maps/MapStates';
import PolymerMapTabView from 'molbio/maps/PolymerMapTabView';
import Range from 'molbio/maps/selectables/Range';
import {Selection} from 'molbio/maps/selectables/Selection';
import AaSequenceAlignmentTabState from 'molbio/tabstates/AaSequenceAlignmentTabState';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import getTestProps from 'test/lib/cypress/getTestProps';
import useOnMount from 'ui-library/hooks/useOnMount';
import {isLeftKey, isRightKey} from 'util/Keyboard';
import {Optional} from 'util/Types';

/**
 * Template component for metadata rendered for each row in an alignment.
 *
 * The two divs are just mountpoints for the real content, but it is important
 * that they remain stable because our Backbone code uses JQuery to register listener
 * on them. Therefore, it's important not to re-render this component.
 */
export function PolymerAlignmentRowTemplate({onMount}: {onMount: () => void}): JSX.Element {
  useOnMount(() => {
    onMount();
  });

  return (
    <>
      <div className="handle alignment-row-metadata-react-mount" />
      {/* We need overflow-x scroll on Chrome for drag scrolling to work,
          but we also want to hide the scrollbar. Firefox sucks and doesn't
          let you non-hackily hide the scrollbar, so for now, we
          set overflow-x: hidden.

          Note: this original comment is old and may be out-of-date.
      */}
      <div
        className="content draggable-svg-container"
        style={firefox ? {overflowX: 'hidden'} : {}}
        {...getTestProps({component: 'PolymerAlignmentRowTemplate', element: 'content'})}
      />
    </>
  );
}

export interface PolymerAlignmentRowViewInitializationOptions<
  M extends AlignmentMapState | AaSequenceAlignmentTabState,
  TabState extends SequenceAlignmentTabState | AaSequenceAlignmentTabState,
> extends ViewOptions<M> {
  tabstate: TabState;
  visibilityOptions?: VisibilityOptions;
  tabView?: SequenceAlignmentTabView;
}

interface MergedModels {
  get(key: 'visibilityOptions'): VisibilityOptions;
}

// TODO: refactor to avoid extending from PolymerMapTabView.
/**
 * An abstract super class for an alignment row that renders a single SliceView. Extends PolymerMapTabView
 * for keyboard support (implementing required methods / deferring implementation to subclasses as noted
 * below). This class provides scroll synchronization with other row views by capturing scroll state
 * in the Range object.
 */
export default abstract class PolymerAlignmentRowView<
  M extends AlignmentMapState | AaSequenceAlignmentTabState,
  TabState extends SequenceAlignmentTabState | AaSequenceAlignmentTabState,
  // @ts-expect-error: this class should not extend PolymerMapTabView - it presents an alignment row not a tab.
> extends PolymerMapTabView<M> {
  override inputSource!: string;
  tabstate!: TabState;
  sliceView?: SliceView;
  visibilityOptions!: VisibilityOptions;
  lastLeft?: number;
  private pendingShowRangeFrame?: number;
  override allowBubblePositionedAboveSlice: boolean = false;

  static override initClass(): void {
    this.prototype.tagName = 'div';
    this.prototype.className = 'alignment-row';
    this.prototype.attributes = {tabindex: 2};
    this.prototype.inputSource = 'alignment-input';
  }

  addPolymersForSearch(): void {}
  removePolymersForSearch(): void {}

  override initialize(options: PolymerAlignmentRowViewInitializationOptions<M, TabState>): void {
    super.initialize(options);
    this.tabstate = options.tabstate!; // Sometimes actually set in ProteinAlignmentRowView.
    const model: MergedModels = this.model;
    this.visibilityOptions = options.visibilityOptions || model.get('visibilityOptions');
  }

  override events(): EventsHash {
    return {
      ...super.events(),
      keydown: 'handleKeydown',
    };
  }

  updateSliceView(): void {
    this.sliceView?.update();
  }

  showRange(left: Optional<number> = null): void {
    this.computeShowRange(left);
    this.applyShowRange();
  }

  /**
   * Read phase of showRange: compute and cache the target scroll offset in `lastLeft`.
   *
   * This is intentionally separate from `applyShowRange` (the write phase) so callers that iterate
   * over many rows can perform all layout reads before any layout writes. Interleaving reads
   * (`svg.width()`) and writes (`scrollLeft`) across rows forces a synchronous reflow on every
   * write, which becomes quadratic in the number of rows on large alignments.
   */
  computeShowRange(left: Optional<number> = null): void {
    // If a left is not specified, compute left from start of range
    if (left == null) {
      const range = this.getRange();
      const rangeStart = range.start();
      const rangeEnd = range.end();

      const xFn = this.getSlice().get('x')!;
      left = Math.floor(xFn(rangeStart));

      if (rangeEnd >= this.tabstate.getPolymerLength()) {
        const $contentDiv = this.$('.content');
        left = $contentDiv.find('svg').width() - $contentDiv.width();
      }
    }
    this.lastLeft = left;
  }

  /**
   * Write phase of showRange: apply the cached scroll offset. See `computeShowRange` for why the
   * read and write phases are separate.
   */
  applyShowRange(): void {
    this.$('.content').scrollLeft(this.lastLeft!);
  }

  private scheduleShowRange(): void {
    if (this.pendingShowRangeFrame != null) {
      return;
    }
    // Defer the initial scroll to the next animation frame. When many rows mount at once, this lets
    // every row's SVG get appended before any `scrollLeft` write forces a synchronous layout, so the
    // browser performs a single layout for the batch instead of one full-document reflow per row
    // (which is quadratic in the number of rows on large alignments).
    this.pendingShowRangeFrame = requestAnimationFrame(() => {
      this.pendingShowRangeFrame = undefined;
      this.showRange();
    });
  }

  setSVGWidth(): void {
    const type = this.tabstate.getType();
    const width = type.width(this.tabstate.getPolymerLength()) + type.letterSpacing;
    d3.select(this.el)
      .select('svg')
      .attr('width', width + 1);
  }

  /* Abstract methods needed to be implemented by subclasses */
  abstract getAlignmentRowType(): ProteinAlignmentKind | null;
  abstract getRange(): Range;
  abstract getRowContentProps(): AlignmentRowInfoWrapperProps;
  abstract getSlice(): Slice;
  abstract makeSliceView(): SliceView;

  // These next three abstract methods are defined by PolymerMapTabView, but
  // expect a mapstate that alignments do not necessarily support, so require that
  // child classes redefine (i.e. override) these.
  abstract override undo(): void;
  abstract override getSelection(): Selection;
  abstract override getResidueSlice(start: number, end: number): string;

  handleArrowPress(e: JQueryEventObject): void {
    e.preventDefault();
    const selection = this.getSelection();
    const shift = e.shiftKey;
    const start = selection.start();
    const end = selection.end();

    // Up and down arrows are handled separately by PolymerAlignmentTabView.
    if (isLeftKey(e)) {
      if (shift) {
        selection.linearExtendBy(-1);
      } else if (selection.isEmpty()) {
        selection.resetBy(-1);
      } else {
        selection.reset(start);
      }
    } else if (isRightKey(e)) {
      if (shift) {
        selection.linearExtendBy(1);
      } else if (selection.isEmpty()) {
        selection.resetBy(1);
      } else {
        selection.reset(end);
      }
    }
  }

  /* Lifecycle methods */
  die(): void {
    if (this.pendingShowRangeFrame != null) {
      cancelAnimationFrame(this.pendingShowRangeFrame);
      this.pendingShowRangeFrame = undefined;
    }
    $(window).off(`.alignment-row-${this.cid}`);
    this.stopListening();
  }

  override remove(): this {
    this.die();
    super.remove();
    return this;
  }

  override stopListening(...args: Array<unknown>): void {
    super.stopListening(...args);
    if (args.length === 0) {
      this.sliceView!.die();
    }
  }

  override registerListeners(): void {
    this.listenTo(this.getSlice(), 'change:height', () => {
      this.tabstate.trigger('rowResize');
    });

    let lastMouseX: number | null = null;
    // Keep track of lastMouseX through window, in case we move off the element
    // and would like a reasonable lastMouseX for updating selection during drag scrolling.

    const $handleDiv = this.$('.handle');
    const $contentDiv = this.$('.content');

    $(window).on(`mousemove.alignment-row-${this.cid}`, (e) => {
      const minX = $handleDiv.offset()!.left + $handleDiv.width() + 2;
      const maxX = this.$el.offset()!.left + this.$el.width();
      lastMouseX = Math.min(Math.max(e.pageX, minX), maxX);
    });

    // If you mousemove onto the handle too fast (and scrollLeft=0), update selection,
    // so you don't get the awkward situation where the first base isn't selected.
    $handleDiv.on('mousemove', () => {
      this.sliceView!.updateSelectionFromScroll(lastMouseX!);
    });

    $contentDiv.on('scroll', (e) => {
      const newLeft = $(e.currentTarget).scrollLeft();
      // If the newLeft position doesn't match what was set in showRange, then
      // this is most likely a user-initiated scroll ($el.scrolLeft(left) triggers scroll event)
      if (newLeft !== this.lastLeft) {
        const xInv = this.getSlice().get('xInv')!;
        const startIndex = Math.floor(xInv(newLeft));
        this.getRange().panStart(startIndex);
        this.tabstate.trigger('scroll', newLeft);
      }

      // If the selection is active, the mouse may now be over a new base.
      this.sliceView!.updateSelectionFromScroll(lastMouseX!);
    });
  }

  renderType(): void {
    switch (this.getAlignmentRowType()) {
      case ALIGNMENT_TYPE.CONSENSUS:
        this.$el.addClass('consensus-alignment');
        break;
      case ALIGNMENT_TYPE.TEMPLATE:
        this.$el.addClass('template-alignment');
        break;
      default:
        break;
    }
  }

  /**
   * Render or re-render the metadata section for this row.
   */
  renderRowMetadata(): void {
    renderToDOM(
      <AlignmentRowInfoWrapper {...this.getRowContentProps()} />,
      this.$el.find('.alignment-row-metadata-react-mount')[0]
    );
  }

  override render(): this {
    this.renderType(); // Append classname onto root div element.
    this.sliceView = this.makeSliceView();
    this.sliceView.setFocusableParent(this.el);

    // Render synchronously so the row's height is known immediately - this allows the virtualizer to accurately
    // determine which rows are actually visible in the viewport. SliceView.render only builds the row SVG
    // and computes its height without measuring the DOM, the DOM append stays deferred to onMount.
    this.sliceView.render();

    renderToDOM(
      <PolymerAlignmentRowTemplate
        onMount={() => {
          // Note: it's important to perform this logic in a callback to ensure that the expected elements
          // are present in the DOM before registering listeners or appending child elements.
          this.renderRowMetadata();
          const $contentDiv = this.$('.content');
          $contentDiv.append(this.sliceView!.el);
          this.registerListeners();
          this.tabstate.trigger('rowResize');
          this.scheduleShowRange();
        }}
      />,
      this.$el[0]
    );

    return this;
  }
}

PolymerAlignmentRowView.initClass();
